<template>
  <a-modal
    :visible="visible"
    :title="title"
    @cancel="handleCancel"
    :footer="null"
    :width="800"
    class="preview-modal"
  >
    <div class="preview-container">
      <!-- 로딩 상태 표시 -->
      <div v-if="loading" class="loading-container">
        <a-spin tip="로딩 중..." />
      </div>
      
      <!-- 이미지 미리보기 - 심플하게 변경 -->
      <div v-else-if="isImage" class="image-container">
        <img
          :src="directStorageUrl"
          :alt="fileName"
          class="preview-image"
        />
      </div>
      
      <!-- PDF 미리보기 -->
      <iframe
        v-else-if="isPDF"
        :src="previewUrl"
        class="preview-pdf"
        type="application/pdf"
      ></iframe>
      
      <!-- 텍스트 파일 미리보기 -->
      <div v-else-if="isText" class="preview-text">
        <pre>{{ textContent }}</pre>
      </div>
      
      <!-- 지원하지 않는 형식 -->
      <div v-else class="preview-unsupported">
        <a-empty description="이 파일 형식은 미리보기를 지원하지 않습니다." />
      </div>
    </div>
  </a-modal>
</template>

<script>
export default {
  name: 'PreviewModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    file: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      textContent: '',
      loading: false,
    };
  },
  computed: {
    title() {
      return this.file?.name || '파일 미리보기';
    },
    fileName() {
      return this.file?.name || '';
    },
    // Azure Blob Storage 직접 URL
    directStorageUrl() {
      if (!this.file) return '';
      
      // 컨테이너 이름 (기본값: certilife)
      const containerName = this.file.container || 'certilife';
      
      // 파일 경로 사용 - 전체 경로 유지
      let filePath = '';
      
      // path가 있으면 우선 사용
      if (this.file.path) {
        filePath = this.file.path;
      } 
      // 없으면 name 사용
      else if (this.file.name) {
        filePath = this.file.name;
      }
      
      // 경로가 없으면 빈 문자열 반환
      if (!filePath) return '';
      
      // Azure Blob Storage 직접 URL 구성
      return `https://certilife.blob.core.windows.net/${containerName}/${filePath}`;
    },
    // 기존 API 기반 URL (PDF, 텍스트 파일용)
    previewUrl() {
      if (!this.file) return '';
      
      // path가 있으면 우선 사용
      let blobPath = '';
      if (this.file.path) {
        blobPath = this.file.path;
      } else if (this.file.name) {
        blobPath = this.file.name;
      }
      
      // 경로가 없으면 빈 문자열 반환
      if (!blobPath) return '';
      
      const encodedPath = encodeURIComponent(blobPath).replace(/%2F/g, '/');
      const container = this.file.container || '';
      const containerParam = container ? `?container=${container}` : '';
      
      return `/object-storages/azure/files/${encodedPath}/preview${containerParam}`;
    },
    isImage() {
      const contentType = this.file?.contentType || '';
      const name = this.file?.name || '';
      return contentType.startsWith('image/') || 
             /\.(jpe?g|png|gif|bmp|webp)$/i.test(name);
    },
    isPDF() {
      const contentType = this.file?.contentType || '';
      const name = this.file?.name || '';
      return contentType === 'application/pdf' || 
             /\.pdf$/i.test(name);
    },
    isText() {
      const contentType = this.file?.contentType || '';
      const name = this.file?.name || '';
      return contentType?.startsWith('text/') || 
             contentType === 'application/json' ||
             /\.(txt|json|html|css|js|xml|md|log)$/i.test(name);
    }
  },
  watch: {
    async visible(newVal) {
      if (newVal) {
        this.loading = true;
        
        if (this.isText) {
          await this.loadTextContent();
        } else {
          // 이미지와 PDF는 짧은 로딩 표시 후 표시
          setTimeout(() => {
            this.loading = false;
          }, 300);
        }
      }
    }
  },
  methods: {
    handleCancel() {
      this.$emit('update:visible', false);
    },
    async loadTextContent() {
      if (!this.file?.path && !this.file?.name) return;
      
      this.loading = true;
      try {
        const response = await this.$apiInstance.get(this.previewUrl, {
          responseType: 'text'
        });
        
        if (response.status !== 200) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        
        this.textContent = response.data;
      } catch (error) {
        console.error('텍스트 파일 로드 실패:', error);
        this.$message.error('텍스트 파일을 불러오는데 실패했습니다.');
        this.textContent = '파일을 불러오는데 실패했습니다.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.preview-modal {
  :deep(.ant-modal-body) {
    padding: 24px;
    max-height: 80vh;
    overflow: auto;
  }
}

.preview-container {
  width: 100%;
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 400px;
}

.image-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-pdf {
  width: 100%;
  height: 70vh;
  border: none;
}

.preview-text {
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  
  pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
}

.preview-unsupported {
  width: 100%;
  padding: 32px;
  text-align: center;
}
</style> 